import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  School,
  Save,
  Close,
} from '@mui/icons-material';
import adminService from '../../../services/admin.service';

interface University {
  _id?: string;
  universityId: string;
  universityName: string;
  universityCity: string;
  universityMail: string;
  createdAt?: string;
  updatedAt?: string;
}

const UniversityManagement: React.FC = () => {
  const [universities, setUniversities] = useState<University[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dialogs
  const [addDialog, setAddDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);

  // Form states
  const [editUniversity, setEditUniversity] = useState<University>({
    universityId: '',
    universityName: '',
    universityCity: '',
    universityMail: '',
  });
  const [selectedUniversity, setSelectedUniversity] = useState<University | null>(null);

  // Bulk add state
  const [bulkUniversities, setBulkUniversities] = useState<University[]>([
    { universityId: '', universityName: '', universityCity: '', universityMail: '' },
  ]);

  useEffect(() => {
    fetchUniversities();
  }, []);

  const fetchUniversities = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await adminService.getInitialConfig();
      if (response.status) {
        setUniversities(response.data?.universities || []);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Üniversiteler yüklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkAdd = async () => {
    const validUniversities = bulkUniversities.filter(
      (u) => u.universityId.trim() && u.universityName.trim() && u.universityCity.trim() && u.universityMail.trim()
    );

    if (validUniversities.length === 0) {
      setError('Lütfen en az bir geçerli üniversite bilgisi girin.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await adminService.addUniversities(validUniversities);
      setSuccess(`${validUniversities.length} üniversite başarıyla eklendi!`);
      setAddDialog(false);
      setBulkUniversities([{ universityId: '', universityName: '', universityCity: '', universityMail: '' }]);
      await fetchUniversities();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Üniversiteler eklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUniversity = async () => {
    if (
      !editUniversity.universityId.trim() ||
      !editUniversity.universityName.trim() ||
      !editUniversity.universityCity.trim() ||
      !editUniversity.universityMail.trim()
    ) {
      setError('Lütfen tüm alanları doldurun.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await adminService.updateUniversities([editUniversity]);
      setSuccess('Üniversite başarıyla güncellendi!');
      setEditDialog(false);
      await fetchUniversities();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Üniversite güncellenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUniversity = async () => {
    if (!selectedUniversity) return;

    setLoading(true);
    setError('');
    try {
      await adminService.deleteUniversity(selectedUniversity.universityId);
      setSuccess('Üniversite başarıyla silindi!');
      setDeleteDialog(false);
      setSelectedUniversity(null);
      await fetchUniversities();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Üniversite silinirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const addBulkUniversityRow = () => {
    setBulkUniversities([
      ...bulkUniversities,
      { universityId: '', universityName: '', universityCity: '', universityMail: '' },
    ]);
  };

  const removeBulkUniversityRow = (index: number) => {
    setBulkUniversities(bulkUniversities.filter((_, i) => i !== index));
  };

  const updateBulkUniversity = (
    index: number,
    field: 'universityId' | 'universityName' | 'universityCity' | 'universityMail',
    value: string
  ) => {
    const updated = [...bulkUniversities];
    updated[index][field] = value;
    setBulkUniversities(updated);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Üniversite Yönetimi
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Sistemdeki üniversiteleri görüntüleyin ve yönetin.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setAddDialog(true)}
          size="large"
        >
          Üniversite Ekle
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Stats */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <School color="primary" sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h3" fontWeight="bold" color="primary">
              {universities.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Toplam Üniversite
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Table */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #e0e0e0' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                <TableCell>Üniversite ID</TableCell>
                <TableCell>Üniversite Adı</TableCell>
                <TableCell>Şehir</TableCell>
                <TableCell>Mail Uzantısı</TableCell>
                <TableCell>Oluşturulma Tarihi</TableCell>
                <TableCell align="right">İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : universities.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <Typography color="text.secondary">
                      Henüz üniversite eklenmemiş.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                universities.map((university) => (
                  <TableRow
                    key={university.universityId}
                    hover
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell>
                      <Chip label={university.universityId} color="secondary" size="small" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="600">
                        {university.universityName}
                      </Typography>
                    </TableCell>
                    <TableCell>{university.universityCity}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color="primary">
                        {university.universityMail}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {university.createdAt
                        ? new Date(university.createdAt).toLocaleDateString('tr-TR')
                        : '-'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        color="primary"
                        onClick={() => {
                          setEditUniversity(university);
                          setEditDialog(true);
                        }}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => {
                          setSelectedUniversity(university);
                          setDeleteDialog(true);
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add Dialog */}
      <Dialog open={addDialog} onClose={() => setAddDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Üniversite Ekle (Bulk)
            <IconButton onClick={() => setAddDialog(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Birden fazla üniversiteyi aynı anda ekleyebilirsiniz.
          </Typography>
          {bulkUniversities.map((university, index) => (
            <Box key={index} sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                <TextField
                  label="Üniversite ID"
                  value={university.universityId}
                  onChange={(e) => updateBulkUniversity(index, 'universityId', e.target.value)}
                  sx={{ width: 150 }}
                  placeholder="uni_001"
                />
                <TextField
                  label="Üniversite Adı"
                  value={university.universityName}
                  onChange={(e) => updateBulkUniversity(index, 'universityName', e.target.value)}
                  fullWidth
                />
                {bulkUniversities.length > 1 && (
                  <IconButton color="error" onClick={() => removeBulkUniversityRow(index)}>
                    <Delete />
                  </IconButton>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Şehir"
                  value={university.universityCity}
                  onChange={(e) => updateBulkUniversity(index, 'universityCity', e.target.value)}
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="Mail Uzantısı"
                  value={university.universityMail}
                  onChange={(e) => updateBulkUniversity(index, 'universityMail', e.target.value)}
                  sx={{ flex: 1 }}
                  placeholder="@ogu.edu.tr"
                />
              </Box>
            </Box>
          ))}
          <Button startIcon={<Add />} onClick={addBulkUniversityRow} variant="outlined" fullWidth>
            Satır Ekle
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialog(false)}>İptal</Button>
          <Button onClick={handleBulkAdd} variant="contained" startIcon={<Save />} disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Üniversite Düzenle</DialogTitle>
        <DialogContent>
          <TextField
            label="Üniversite ID"
            value={editUniversity.universityId}
            onChange={(e) =>
              setEditUniversity({ ...editUniversity, universityId: e.target.value })
            }
            fullWidth
            sx={{ mb: 2, mt: 2 }}
            disabled
          />
          <TextField
            label="Üniversite Adı"
            value={editUniversity.universityName}
            onChange={(e) =>
              setEditUniversity({ ...editUniversity, universityName: e.target.value })
            }
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            label="Şehir"
            value={editUniversity.universityCity}
            onChange={(e) =>
              setEditUniversity({ ...editUniversity, universityCity: e.target.value })
            }
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            label="Mail Uzantısı"
            value={editUniversity.universityMail}
            onChange={(e) =>
              setEditUniversity({ ...editUniversity, universityMail: e.target.value })
            }
            fullWidth
            placeholder="@ogu.edu.tr"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>İptal</Button>
          <Button onClick={handleUpdateUniversity} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Güncelle'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Üniversite Sil</DialogTitle>
        <DialogContent>
          <Typography>
            <strong>{selectedUniversity?.universityName}</strong> ({selectedUniversity?.universityId}
            ) üniversitesini silmek istediğinizden emin misiniz?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>İptal</Button>
          <Button onClick={handleDeleteUniversity} color="error" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Sil'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UniversityManagement;

